import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../lib/api";
import StatusBadge from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { generateQuotePdf } from "../lib/pdf";
import { ArrowLeft, Plus, Eye, Download, FileText, Building2, Mail, ClipboardList, Phone, Calendar, StickyNote, Trash2, Users, Bell, Check } from "lucide-react";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";

const fmt = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const NOTE_TYPES = [
  { value: "note", label: "Note", icon: StickyNote, bg: "bg-zinc-50 border-zinc-200", color: "text-zinc-600" },
  { value: "call", label: "Call", icon: Phone, bg: "bg-blue-50 border-blue-100", color: "text-blue-600" },
  { value: "email", label: "Email", icon: Mail, bg: "bg-purple-50 border-purple-100", color: "text-primary" },
  { value: "meeting", label: "Meeting", icon: Calendar, bg: "bg-amber-50 border-amber-100", color: "text-amber-600" },
];

export default function AdminClientDetail() {
  const navigate = useNavigate();
  const { email: rawEmail } = useParams();
  const email = (rawEmail || "").toLowerCase();
  const todayStr = new Date().toISOString().slice(0, 10);

  const [quotes, setQuotes] = useState([]);
  const [onboardings, setOnboardings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState([]);
  const [noteBody, setNoteBody] = useState("");
  const [noteType, setNoteType] = useState("note");
  const [noteRemind, setNoteRemind] = useState("");
  const [savingNote, setSavingNote] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/quotes"), api.get("/onboarding")])
      .then(([q, o]) => {
        setQuotes(q.data.filter((x) => (x.client_email || "").toLowerCase() === email));
        setOnboardings(o.data.filter((x) => (x.client_email || "").toLowerCase() === email));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
    api.get(`/admin/clients/${encodeURIComponent(email)}/notes`)
      .then(({ data }) => setNotes(data))
      .catch(() => {});
  }, [email]);

  const addNote = async () => {
    const body = noteBody.trim();
    if (!body) return;
    setSavingNote(true);
    try {
      const { data } = await api.post(`/admin/clients/${encodeURIComponent(email)}/notes`, { body, type: noteType, remind_at: noteRemind || null });
      setNotes((n) => [data, ...n]);
      setNoteBody("");
      setNoteType("note");
      setNoteRemind("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save note");
    } finally {
      setSavingNote(false);
    }
  };

  const markDone = async (id) => {
    try {
      await api.post(`/admin/clients/${encodeURIComponent(email)}/notes/${id}/done`);
      setNotes((n) => n.map((x) => (x.id === id ? { ...x, done: true } : x)));
      toast.success("Follow-up marked done");
    } catch (e) {
      toast.error("Could not update follow-up");
    }
  };

  const deleteNote = async (id) => {
    try {
      await api.delete(`/admin/clients/${encodeURIComponent(email)}/notes/${id}`);
      setNotes((n) => n.filter((x) => x.id !== id));
    } catch (e) {
      toast.error("Could not delete note");
    }
  };

  const name = quotes[0]?.client_name || email;
  const company = quotes.find((q) => q.company)?.company || "";
  const totalQuoted = quotes.reduce((s, q) => s + (q.one_time_total || 0), 0);
  const totalPaid = quotes.reduce((s, q) => s + (q.deposit_paid ? (q.deposit_amount || 0) : 0), 0);
  const isLead = quotes.some((q) => q.source === "public");
  const latestOb = onboardings[0];

  const newQuote = () => {
    sessionStorage.setItem("builderPrefill", JSON.stringify({ form: { client_name: name, client_email: email, company } }));
    navigate("/build");
  };

  const obLabel = (s) => ({ sent: "Sent — awaiting client", in_progress: "In progress", submitted: "Submitted — review", complete: "Complete" }[s] || s || "—");
  const obColor = (s) => ({ sent: "bg-blue-50 text-blue-700 border-blue-100", in_progress: "bg-amber-50 text-amber-700 border-amber-100", submitted: "bg-green-50 text-green-700 border-green-100", complete: "bg-zinc-100 text-zinc-700 border-zinc-200" }[s] || "bg-zinc-50 text-zinc-600 border-zinc-200");

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="admin-client-detail">
      <button onClick={() => navigate("/admin/clients")} className="font-mono text-xs uppercase tracking-[0.2em] text-primary flex items-center gap-1 hover:underline" data-testid="back-to-clients">
        <ArrowLeft className="h-3 w-3" /> Clients
      </button>

      <div className="flex flex-wrap items-start justify-between gap-4 mt-2 mb-8">
        <div>
          <h1 className="font-display font-black text-4xl tracking-tighter flex items-center gap-3 flex-wrap">
            {name}
            {isLead && <span className="font-mono text-[10px] uppercase tracking-wider bg-primary/10 text-primary px-2 py-1">Lead</span>}
          </h1>
          <div className="mt-3 flex flex-col gap-1 font-mono text-xs text-zinc-600">
            <span className="flex items-center gap-2"><Mail className="h-3.5 w-3.5 text-zinc-400" /> {email}</span>
            {company && <span className="flex items-center gap-2"><Building2 className="h-3.5 w-3.5 text-zinc-400" /> {company}</span>}
          </div>
        </div>
        <Button onClick={newQuote} data-testid="client-new-quote-btn" className="rounded-none bg-primary hover:bg-[#5E1F92] text-white font-mono uppercase tracking-widest">
          <Plus className="h-4 w-4 mr-1" /> New quote for this client
        </Button>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-8">
        <div className="bg-blue-50 border border-blue-100 p-5">
          <p className="font-mono text-xs uppercase tracking-widest text-zinc-500">Quotes</p>
          <p className="mt-1 font-display font-black text-2xl tracking-tighter" data-testid="client-quote-count">{quotes.length}</p>
        </div>
        <div className="bg-green-50 border border-green-100 p-5">
          <p className="font-mono text-xs uppercase tracking-widest text-zinc-500">Total Quoted</p>
          <p className="mt-1 font-display font-black text-2xl tracking-tighter">{fmt(totalQuoted)}</p>
        </div>
        <div className="bg-red-50 border border-red-100 p-5">
          <p className="font-mono text-xs uppercase tracking-widest text-zinc-500">Total Paid</p>
          <p className="mt-1 font-display font-black text-2xl tracking-tighter text-green-700">{fmt(totalPaid)}</p>
        </div>
      </div>

      {/* Onboarding status */}
      <div className="border border-zinc-200 bg-white p-5 mb-8 flex flex-wrap items-center justify-between gap-3" data-testid="client-onboarding-status">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 bg-purple-50 border border-purple-100 flex items-center justify-center shrink-0">
            <ClipboardList className="h-4 w-4 text-primary" />
          </div>
          <div>
            <p className="font-display font-bold">Onboarding</p>
            {latestOb ? (
              <span className={`inline-block mt-1 font-mono text-[10px] uppercase tracking-widest border px-2 py-0.5 ${obColor(latestOb.status)}`}>{obLabel(latestOb.status)}</span>
            ) : (
              <p className="text-sm text-zinc-500 mt-0.5">No questionnaire sent yet.</p>
            )}
          </div>
        </div>
        {latestOb && (
          <Button onClick={() => navigate("/admin/onboarding")} variant="outline" data-testid="client-view-onboarding-btn" className="rounded-none border-zinc-900 font-mono uppercase tracking-widest text-xs">
            View responses
          </Button>
        )}
      </div>

      {/* Activity / Notes timeline */}
      <div className="border border-zinc-200 bg-white p-5 mb-8" data-testid="client-activity">
        <h2 className="font-mono text-xs uppercase tracking-widest text-zinc-600 mb-4">Activity &amp; Notes</h2>
        <div className="mb-5">
          <Textarea
            data-testid="note-input"
            value={noteBody}
            onChange={(e) => setNoteBody(e.target.value)}
            placeholder="Log a call, meeting, or add a follow-up note…"
            className="rounded-none border-zinc-300 focus-visible:ring-primary min-h-[72px]"
          />
          <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
            <div className="flex gap-1 flex-wrap items-center">
              {NOTE_TYPES.map((t) => (
                <button
                  key={t.value}
                  data-testid={`note-type-${t.value}`}
                  onClick={() => setNoteType(t.value)}
                  className={`flex items-center gap-1 px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-widest border transition-colors ${noteType === t.value ? "border-primary text-primary bg-primary/5" : "border-zinc-200 text-zinc-500 hover:border-zinc-400"}`}
                >
                  <t.icon className="h-3 w-3" /> {t.label}
                </button>
              ))}
              <label className="flex items-center gap-1.5 ml-1 pl-2 border-l border-zinc-200" title="Set a follow-up reminder date">
                <Bell className="h-3 w-3 text-zinc-400" />
                <input
                  type="date"
                  data-testid="note-remind-input"
                  value={noteRemind}
                  onChange={(e) => setNoteRemind(e.target.value)}
                  className="font-mono text-[11px] text-zinc-600 border border-zinc-200 px-1.5 py-1 outline-none focus:border-primary"
                />
                {noteRemind && (
                  <button onClick={() => setNoteRemind("")} data-testid="note-remind-clear" className="text-zinc-400 hover:text-red-600" title="Clear reminder">✕</button>
                )}
              </label>
            </div>
            <Button onClick={addNote} disabled={savingNote || !noteBody.trim()} data-testid="add-note-btn" className="rounded-none bg-zinc-900 hover:bg-zinc-700 text-white font-mono uppercase tracking-widest text-xs disabled:opacity-40">
              <Plus className="h-4 w-4 mr-1" /> {savingNote ? "Saving…" : "Add"}
            </Button>
          </div>
        </div>

        {notes.length === 0 ? (
          <p className="text-sm text-zinc-400 font-mono">No activity logged yet.</p>
        ) : (
          <ul className="space-y-3">
            {notes.map((n) => {
              const t = NOTE_TYPES.find((x) => x.value === n.type) || NOTE_TYPES[0];
              return (
                <li key={n.id} data-testid={`note-${n.id}`} className="flex gap-3 group">
                  <div className={`h-8 w-8 shrink-0 border flex items-center justify-center ${t.bg}`}>
                    <t.icon className={`h-4 w-4 ${t.color}`} />
                  </div>
                  <div className="flex-1 min-w-0 border-b border-zinc-100 pb-3">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-400">
                        {t.label} · {n.author} · {new Date(n.created_at).toLocaleString()}
                      </span>
                      <button onClick={() => deleteNote(n.id)} data-testid={`delete-note-${n.id}`} className="opacity-0 group-hover:opacity-100 text-zinc-400 hover:text-red-600 transition-opacity" title="Delete">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    <p className="mt-1 text-sm text-zinc-800 whitespace-pre-wrap break-words">{n.body}</p>
                    {n.remind_at && (
                      n.done ? (
                        <span data-testid={`note-followup-done-${n.id}`} className="mt-2 inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-green-600">
                          <Check className="h-3 w-3" /> Followed up
                        </span>
                      ) : (
                        <div className="mt-2 flex items-center gap-2">
                          <span data-testid={`note-followup-${n.id}`} className={`inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest border px-2 py-0.5 ${n.remind_at < todayStr ? "bg-red-50 text-red-700 border-red-200" : "bg-amber-50 text-amber-700 border-amber-100"}`}>
                            <Bell className="h-3 w-3" /> Follow up {new Date(n.remind_at + "T00:00:00").toLocaleDateString()}{n.remind_at < todayStr ? " · overdue" : ""}
                          </span>
                          <button onClick={() => markDone(n.id)} data-testid={`note-mark-done-${n.id}`} className="font-mono text-[10px] uppercase tracking-widest text-primary hover:underline">Mark done</button>
                        </div>
                      )
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {loading ? (
        <p className="font-mono text-sm text-zinc-500">Loading…</p>
      ) : (
        <>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-mono text-xs uppercase tracking-widest text-zinc-600">Quotes</h2>
          <button onClick={() => navigate(`/admin/quotes?client=${encodeURIComponent(email)}`)} data-testid="client-view-all-quotes" className="font-mono text-xs uppercase tracking-widest text-primary hover:underline">
            Open in quotes table →
          </button>
        </div>
        <div className="border border-zinc-200 bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-200 font-mono text-xs uppercase tracking-widest text-zinc-500">
                <th className="text-left px-4 py-3">Quote</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">One-time</th>
                <th className="text-right px-4 py-3">Monthly</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {quotes.map((q) => (
                <tr key={q.id} data-testid={`client-quote-row-${q.id}`} className="border-b border-zinc-100 hover:bg-zinc-50">
                  <td className="px-4 py-3">
                    <button onClick={() => navigate(`/quotes/${q.id}`)} className="font-medium hover:text-primary">#{q.id.slice(-8).toUpperCase()}</button>
                    <p className="text-xs text-zinc-400 font-mono">{new Date(q.created_at).toLocaleDateString()}</p>
                  </td>
                  <td className="px-4 py-3"><StatusBadge status={q.status} /></td>
                  <td className="px-4 py-3 text-right font-mono">{fmt(q.one_time_total)}</td>
                  <td className="px-4 py-3 text-right font-mono text-zinc-500">{q.monthly_total ? `${fmt(q.monthly_total)}/mo` : "—"}</td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1">
                      <Button size="sm" variant="ghost" data-testid={`client-quote-view-${q.id}`} onClick={() => navigate(`/quotes/${q.id}`)} className="rounded-none h-8 px-2" title="View"><Eye className="h-3.5 w-3.5" /></Button>
                      <Button size="sm" variant="outline" data-testid={`client-quote-download-${q.id}`} onClick={() => generateQuotePdf(q)} className="rounded-none h-8 border-zinc-300 px-2" title="Download PDF"><Download className="h-3.5 w-3.5" /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {quotes.length === 0 && <p className="p-10 text-center text-zinc-500">No quotes for this client yet.</p>}
        </div>
        </>
      )}
    </div>
  );
}
